import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Gauge, Check } from 'lucide-react';
import VehicleImage from './VehicleImage';
import { Reveal, SectionHeading } from './Reveal';
import { VEHICLES } from '../data/content';

const FleetCard = ({ v, i, onReserve, onDetails }) => (
  <Reveal delay={i * 0.12} className="h-full">
    <article
      data-testid={`fleet-card-${v.id}`}
      className="group flex h-full flex-col border border-white/5 bg-surface transition-all duration-500 hover:-translate-y-2 hover:border-gold/30 hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)]"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <VehicleImage src={v.image} alt={v.name} label={v.name.toUpperCase()} />
        <div className="absolute left-0 top-0 h-[2px] w-0 bg-gold transition-all duration-700 group-hover:w-full" />
        <span data-testid={`fleet-price-${v.id}`} className="absolute bottom-4 right-4 bg-ink/80 px-4 py-2 text-sm font-semibold tracking-wide text-gold backdrop-blur-sm">
          ${v.price}<span className="font-light text-white/60">/day</span>
        </span>
      </div>
      <div className="flex flex-1 flex-col p-7">
        <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold/80">{v.category}</span>
        <h3 className="font-display mt-2 text-2xl tracking-tight text-white">{v.name}</h3>
        <p className="mt-3 text-sm font-light leading-relaxed text-white/55">{v.description}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {v.specs.slice(0, 3).map((s) => (
            <span key={s} className="border border-white/10 px-3 py-1 text-[11px] uppercase tracking-wider text-white/50">{s}</span>
          ))}
          <span className="border border-white/10 px-3 py-1 text-[11px] uppercase tracking-wider text-white/50">{v.miles}</span>
        </div>
        <div className="mt-auto flex gap-3 pt-7">
          <button
            data-testid={`fleet-book-${v.id}`}
            onClick={() => onReserve(v.name)}
            className="flex-1 bg-gold py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-bright"
          >
            Book Now
          </button>
          <button
            data-testid={`fleet-details-${v.id}`}
            onClick={() => onDetails(v)}
            className="flex-1 border border-white/15 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            View Details
          </button>
        </div>
      </div>
    </article>
  </Reveal>
);

const ModalBody = ({ v, onClose, onReserve }) => {
  const [active, setActive] = useState(0);
  const photos = v.gallery && v.gallery.length ? v.gallery : [v.image];
  return (
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
      className="max-h-[90vh] w-full max-w-2xl overflow-y-auto border border-white/10 bg-surface"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <VehicleImage src={photos[active]} alt={`${v.name} photo ${active + 1}`} label={v.name.toUpperCase()} />
        <button data-testid="vehicle-details-close" onClick={onClose} aria-label="Close details" className="absolute right-4 top-4 bg-ink/70 p-2 text-white transition-colors hover:text-gold">
          <X className="h-5 w-5" />
        </button>
      </div>
      {photos.length > 1 && (
        <div data-testid="vehicle-gallery" className="flex gap-2 border-b border-white/10 bg-ink/40 p-3">
          {photos.map((p, idx) => (
            <button
              key={p}
              data-testid={`vehicle-gallery-thumb-${idx}`}
              onClick={() => setActive(idx)}
              aria-label={`View photo ${idx + 1}`}
              className={`relative aspect-[16/10] w-20 overflow-hidden border transition-colors duration-300 ${idx === active ? 'border-gold' : 'border-white/10 opacity-50 hover:opacity-90'}`}
            >
              <img src={p} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="p-8">
            <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold">{v.category}</span>
            <h3 className="font-display mt-2 text-3xl tracking-tight text-white">{v.name}</h3>
            <p className="mt-4 text-sm font-light leading-relaxed text-white/60">{v.description}</p>
            <div className="mt-6 grid grid-cols-2 gap-4 border-t border-white/10 pt-6 text-sm">
              <div className="flex items-center gap-3 text-white/70"><Gauge className="h-4 w-4 text-gold" /> {v.miles} included</div>
              <div className="flex items-center gap-3 text-white/70"><Users className="h-4 w-4 text-gold" /> {v.category}</div>
            </div>
            <ul className="mt-6 grid grid-cols-2 gap-3">
              {v.specs.map((s) => (
                <li key={s} className="flex items-center gap-2 text-sm text-white/70">
                  <Check className="h-4 w-4 text-gold" /> {s}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
              <span className="font-display text-3xl text-gold">${v.price}<span className="text-base font-light text-white/50">/day</span></span>
              <button
                data-testid="vehicle-details-book"
                onClick={() => { onClose(); onReserve(v.name); }}
                className="bg-gold px-8 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-gold-bright"
              >
                Book Now
              </button>
            </div>
      </div>
    </motion.div>
  );
};

const DetailsModal = ({ v, onClose, onReserve }) => (
  <AnimatePresence>
    {v && (
      <motion.div
        data-testid="vehicle-details-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
        onClick={onClose}
      >
        <ModalBody key={v.id} v={v} onClose={onClose} onReserve={onReserve} />
      </motion.div>
    )}
  </AnimatePresence>
);

const Fleet = ({ onReserve }) => {
  const [selected, setSelected] = useState(null);
  return (
    <section id="fleet" data-testid="fleet-section" className="relative mx-auto max-w-7xl scroll-mt-24 px-6 py-28 lg:px-10 lg:py-36">
      <SectionHeading eyebrow="The Fleet" title={<>Curated. Detailed.<br /><span className="italic text-gold">Ready.</span></>} />
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {VEHICLES.map((v, i) => (
          <FleetCard key={v.id} v={v} i={i} onReserve={onReserve} onDetails={setSelected} />
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="mt-16 flex flex-col items-center justify-between gap-6 border border-gold/20 bg-surface px-8 py-8 md:flex-row">
          <p className="text-center md:text-left">
            <span className="text-xs font-medium uppercase tracking-[0.3em] text-gold">Own a luxury vehicle?</span>
            <span className="font-display mt-2 block text-2xl tracking-tight text-white">Turn it into income with ALAS Velocity.</span>
          </p>
          <a
            data-testid="fleet-partner-cta"
            href="#partner"
            onClick={(e) => { e.preventDefault(); document.querySelector('#partner')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="group flex shrink-0 items-center gap-3 border border-gold/50 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold/10"
          >
            Partner With ALAS Velocity
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </a>
        </div>
      </Reveal>
      <DetailsModal v={selected} onClose={() => setSelected(null)} onReserve={onReserve} />
    </section>
  );
};

export default Fleet;
